import React from "react";
import { NavLink } from "react-router";

// Nav row (prototype chrome-itinerary.jsx NavRow): sticky white card with the four tabs,
// booking ref + Log Out on the right. On mobile the tabs become a 2-column grid of soft-filled
// buttons with a ref/logout row underneath (prototype r-nav rules) — nothing hides in a menu.
const allNavItems = [
    { to: "/", label: "My Booking", icon: BookingIcon },
    { to: "/payment", label: "Make payment", icon: PaymentIcon, directOnly: true },
    { to: "/documentation", label: "View documents", icon: DocumentsIcon },
    { to: "/important-information", label: "Important information", icon: ErrataIcon },
];

// Prototype tab glyphs (18px, stroke 2).
function BookingIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <rect x="5" y="4" width="14" height="18" rx="2" /><path d="M9 4V2h6v2" /><path d="M9 12l2 2 4-4" />
        </svg>
    );
}

function PaymentIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
        </svg>
    );
}

function DocumentsIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
        </svg>
    );
}

function ErrataIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    );
}

const LogoutIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const Navigation = ({ onLogout, authenticationLevel, bookingReference }) => {
    const navItems = allNavItems.filter(item => !item.directOnly || authenticationLevel === "DirectByPax");

    const desktopLink = ({ isActive }) =>
        `flex items-center gap-2 px-4 py-2.5 rounded-btn text-sm transition ${isActive
            ? "bg-emp text-white font-semibold"
            : "text-primary font-medium"
        }`;

    const mobileLink = ({ isActive }) =>
        `flex items-center gap-1.5 px-2.5 py-[0.6875rem] rounded-[0.625rem] text-[0.78125rem] leading-tight min-w-0 transition ${isActive
            ? "bg-emp text-white font-semibold"
            : "bg-bg_2 text-primary font-medium"
        }`;

    const refBadge = bookingReference && (
        <span className="text-sm text-content_2 whitespace-nowrap">Ref: <strong className="text-primary">{bookingReference}</strong></span>
    );

    const logoutButton = (
        <button onClick={onLogout}
                className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-primary cursor-pointer whitespace-nowrap">
            <LogoutIcon />
            Log Out
        </button>
    );

    return (
        <nav className="bg-white rounded-card mb-[1.125rem] sticky top-[0.625rem] z-50 shadow-nav">
            {/* Desktop navigation */}
            <div className="hidden md:flex items-center justify-between px-[1.375rem] py-3.5">
                <div className="flex items-center gap-1.5">
                    {navItems.map(({ to, label, icon: Icon }) => (
                        <NavLink key={to} to={to} end={to === "/"} className={desktopLink}>
                            <Icon />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </div>
                <div className="flex items-center gap-[1.125rem]">
                    {refBadge}
                    {logoutButton}
                </div>
            </div>

            {/* Mobile navigation: 2-col tab grid + ref/logout row (prototype r-nav) */}
            <div className="md:hidden flex flex-col gap-3 p-3">
                <div className="grid grid-cols-2 gap-2">
                    {navItems.map(({ to, label, icon: Icon }) => (
                        <NavLink key={to} to={to} end={to === "/"} className={mobileLink}>
                            <Icon />
                            {/* Labels wrap rather than truncate: "Important information" doesn't fit
                                a half-width tab on narrow phones and must never lose its second word. */}
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </div>
                <div className="flex items-center justify-between gap-3">
                    {refBadge}
                    {logoutButton}
                </div>
            </div>
        </nav>
    );
};

export default Navigation;
